import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { BellRing, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { upcomingBills } from "../lib/finance";
import { fmt, dateLabel } from "../lib/format";
import { Banner } from "./common";
import { Button } from "./ui/button";

const SENT = "hb-reminders-sent";
const when = (n) => (n <= 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`);

export const useUpcomingBills = () => {
  const { state } = useStore();
  return useMemo(() => upcomingBills(state, state.settings.reminderDays ?? 3), [state]);
};

export const notifySupported = () => typeof window !== "undefined" && "Notification" in window;

async function show(title, body, tag) {
  const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration().catch(() => null) : null;
  if (reg?.showNotification) return reg.showNotification(title, { body, tag, icon: "/icon.svg", data: { url: "/bills?tab=calendar" } });
  new Notification(title, { body, tag, icon: "/icon.svg" });
}

// Sends one browser notification per upcoming bill payment (once per device)
export function ReminderNotifier() {
  const { state } = useStore();
  const bills = useUpcomingBills();
  const on = state.settings.reminderNotify && notifySupported() && Notification.permission === "granted";
  useEffect(() => {
    if (!on) return;
    const run = () => {
      const sent = new Set(JSON.parse(localStorage.getItem(SENT) || "[]"));
      const fresh = bills.filter((b) => !sent.has(b.key));
      fresh.forEach((b) => show(`${b.name} due ${when(b.inDays)}`, `${fmt(Math.abs(b.amount))} on ${dateLabel(b.date)}${b.confirmed ? "" : " (estimated)"}`, b.key).catch(() => {}));
      if (fresh.length) localStorage.setItem(SENT, JSON.stringify([...sent, ...fresh.map((b) => b.key)].slice(-300)));
    };
    run();
    const t = setInterval(run, 60 * 60 * 1000);
    return () => clearInterval(t);
  }, [on, bills]);
  return null;
}

export function BillReminderBanner({ limit = 4 }) {
  const { state, update } = useStore();
  const bills = useUpcomingBills();
  if (!bills.length) return null;
  const total = bills.reduce((s, b) => s + Math.abs(b.amount), 0);
  const dismiss = (keys) => update("Dismiss bill reminder", (d) => (d.dismissedReminders = [...(d.dismissedReminders || []), ...keys].slice(-500)));
  return (
    <Banner tone="warning" icon={BellRing} testid="bill-reminder-banner" action={<Button variant="ghost" size="icon" className="h-7 w-7" title="Dismiss all" onClick={() => dismiss(bills.map((b) => b.key))} data-testid="bill-reminder-dismiss-all"><X className="h-4 w-4" /></Button>}>
      <b>{bills.length} bill{bills.length === 1 ? "" : "s"} due in the next {state.settings.reminderDays ?? 3} day{(state.settings.reminderDays ?? 3) === 1 ? "" : "s"}</b> · {fmt(total)} total
      <ul className="mt-1.5 space-y-0.5">
        {bills.slice(0, limit).map((b) => (
          <li key={b.key} className="flex items-center gap-2 text-sm" data-testid={`bill-reminder-${b.itemKey}`}>
            <span className="flex-1">{b.name} — <span className="num">{fmt(Math.abs(b.amount))}</span> <span className="text-muted-foreground">{when(b.inDays)} ({dateLabel(b.date)}){b.confirmed ? "" : " · estimated"}</span></span>
            <button className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => dismiss([b.key])} data-testid={`bill-reminder-dismiss-${b.itemKey}`}>Dismiss</button>
          </li>
        ))}
      </ul>
      {bills.length > limit && <Link to="/bills?tab=calendar" className="text-xs text-primary underline">+{bills.length - limit} more on the calendar</Link>}
    </Banner>
  );
}
